/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { API } from "../../../globals/globals";
import { useExportAlarmsMutation, useGetAlarmsQuery } from "../../../store/api/devices/deviceApiSlice";
import { useSelector } from "react-redux";
import { alarmColumns } from "../../../components/Shared/Table/Columns";
import { useLocation } from "react-router-dom";
import { searchKeywordByScopeSelector, searchKeywordSelector } from "../../../store/search/selector";
import { setAlarmsCount, setDeviceCount, setOpenToast } from "../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../store/hooks";
import { paginationModelSelector, selectedNavListItemSelector } from "../../../store/helpers/helperSelectors";
import { errorMessage, exportInCSV } from "../../../utils/helpers";
import { initialState } from "../../../store/helpers/helperInitialState";
import { urlParameterSelector } from "../../../store/filters/events/EventFilterSelectors";
import { urlParameterAlarmsSelector } from "../../../store/filters/alarms/selectors";

export const useAlarmDisplay = (selectedGeoRegions?: string[]) => {
  const dispatch = useStoreDispatch();
  const location = useLocation();
  const locationPath = location?.pathname.split("/");
  const deviceKeyFromPath = locationPath?.includes("device") ? locationPath[2] : undefined;
  const filters = useSelector((state: any) => urlParameterAlarmsSelector(state, selectedGeoRegions));
  const serverFilters = useSelector((state: any) => urlParameterAlarmsSelector(state, selectedGeoRegions));
  const keyword = useSelector(searchKeywordByScopeSelector("alarms"));
  //filter alarms based on the selected group
  const selectedGroupKey = useSelector(selectedNavListItemSelector);
  const { alarmDisplay = initialState.pagination } = useSelector(paginationModelSelector);
  const [exportAlarms, exportStatus] = useExportAlarmsMutation();
  const [sortOrder, setAlarmTableSort] = useState("retention,desc");

  filters.set("sort", sortOrder);
  filters.set("page", alarmDisplay.page.toString());
  filters.set("size", alarmDisplay.pageSize.toString());
  filters.set("search", keyword);
  // filters.set("isAlarm", "true");

  switch (selectedGroupKey) {
    case "devices": {
      filters.set("deviceSpecs", "");
      break;
    }
    case "disconnected": {
      filters.set("deviceSpecs", "");
      break;
    }
    case "maintenancemode": {
      filters.set("deviceSpecs", "");
      break;
    }

    case "ungrouped": {
      filters.set("deviceSpecs", "");
      // filters.set("deviceSpecs", "parentKey,''");
      break;
    }

    case "pmCollected": {
      filters.set("deviceSpecs", "")
      break;
    }

    default: {
      if (!!selectedGroupKey) {
        filters.set("deviceSpecs", "");
        // filters.set("deviceSpecs", `parentKey,${selectedGroupKeyValue}`);
      }
      break;
    }
  }

  const filtersString = filters.toString();
  const args = deviceKeyFromPath
    ? { filters: filtersString, deviceKey: deviceKeyFromPath }
    : { filters: filtersString };

  const { data, isLoading } = useGetAlarmsQuery(args, {
    pollingInterval: API.ENDPOINTS.ALARMS.pollingInterval || API.POLLING_INTERVAL
  });

  useEffect(() => {
    dispatch(
      setDeviceCount({
        disconnectedDeviceCount: Number(localStorage.getItem("disconnected")),
        allDeviceCount: Number(localStorage.getItem("allDevice")),
        inMaintenanceModeCount: Number(localStorage.getItem("inMaintenanceMode")),
        ungroupedCount: Number(localStorage.getItem("ungrouped")),
        kpiSyncErrorCount: Number(localStorage.getItem("kpiErorrCount")),
        offLineDeviceCount: Number(localStorage.getItem("offLineDeviceCount")),
        configMismatchCount: Number(localStorage.getItem("configMismatchCount")),
      })
    );
  }, [dispatch]);

  useEffect(() => {
    dispatch(setAlarmsCount(data?.totalItems || 0));
  }, [data?.totalItems, dispatch]);

  const exportHandler = async () => {
    try {
      const response: any = await exportAlarms(args);
      if (response?.error) throw response?.error;
      exportInCSV(response, "Alarms");
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  return {
    data,
    isLoading,
    alarmColumns,
    exportHandler,
    exportStatus,
    setAlarmTableSort,
  };
};